import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import LogoutIcon from '@mui/icons-material/Logout';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import api from '../services/api';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.white,
  borderBottom: '1px solid rgba(0, 255, 159, 0.1)',
  padding: '6px 8px',
  fontSize: 12.5,
  lineHeight: 1.35,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  verticalAlign: 'top'
}));

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: 'rgba(0, 30, 60, 0.5)'
  },
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 20, 40, 0.5)'
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 255, 159, 0.1) !important'
  },
  '&:last-child td, &:last-child th': {
    borderBottom: 0
  }
}));

const SummaryCard = styled(Paper)(() => ({
  background: 'linear-gradient(135deg, rgba(0, 120, 255, 0.15), rgba(0, 255, 159, 0.2))',
  border: '1px solid rgba(0, 255, 159, 0.2)',
  borderRadius: 12,
  padding: 14,
  height: '100%'
}));

const formatSummaryValue = (value) =>
  typeof value === 'number' ? value.toLocaleString() : value || '0';

const formatNumber = (value, fallback = '0') => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return fallback;
  }
  return numeric.toLocaleString();
};

const formatDisplayId = (value) => {
  if (!value) {
    return '—';
  }
  const str = String(value);
  if (str.length <= 5) {
    return str;
  }
  return `${str.slice(0, 4)}…`;
};

const ROLE_LABELS = {
  admin: '管',
  user: '普'
};

const STATUS_LABELS = {
  active: '正',
  normal: '正',
  disabled: '停',
  inactive: '停',
  banned: '禁'
};

const ONLINE_LABELS = {
  online: '在',
  offline: '离'
};

const ACCESS_STATUS_LABELS = {
  unrestricted: '无限制',
  trialOnly: '测试模式',
  fullAccess: '永久通行'
};

const ACCESS_STATUS_SHORT = {
  unrestricted: '无',
  trialOnly: '试',
  fullAccess: '全'
};

const ACCESS_STATUS_STYLES = {
  unrestricted: {
    bg: 'rgba(0, 188, 212, 0.15)',
    color: '#4dd0e1',
    border: '1px solid rgba(0, 188, 212, 0.4)'
  },
  trialOnly: {
    bg: 'rgba(255, 193, 7, 0.18)',
    color: '#ffca28',
    border: '1px solid rgba(255, 193, 7, 0.4)'
  },
  fullAccess: {
    bg: 'rgba(76, 175, 80, 0.18)',
    color: '#81c784',
    border: '1px solid rgba(76, 175, 80, 0.4)'
  }
};

const formatDate = (value) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const UserManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [actionLoading, setActionLoading] = useState({
    reset: {},
    logout: {},
    role: {}
  });

  const updateActionLoading = (actionKey, userId, loading) => {
    if (!actionKey || !userId) {
      return;
    }
    setActionLoading((prev) => ({
      ...prev,
      [actionKey]: {
        ...(prev[actionKey] || {}),
        [userId]: loading
      }
    }));
  };

  const loadUsers = async (options = {}) => {
    setLoading(true);
    try {
      const response = await api.getAllUsers({
        page: options.page ?? (page + 1),
        limit: options.limit ?? rowsPerPage,
        search: options.search ?? ''
      });

      let nextUsers = [];
      let nextTotal = 0;

      if (response?.success && Array.isArray(response.users)) {
        nextUsers = response.users;
        nextTotal = response.pagination?.total ?? response.users.length;
      } else if (Array.isArray(response)) {
        nextUsers = response;
        nextTotal = response.length;
      } else if (response?.data && Array.isArray(response.data)) {
        nextUsers = response.data;
        nextTotal = response.data.length;
      }

      setUsers(nextUsers);
      setTotalCount(nextTotal);
    } catch (error) {
      console.error('获取用户数据失败:', error);
        setSnackbar({
          open: true,
        message: error.userMessage || '获取用户数据失败',
          severity: 'error'
        });
    } finally {
      setLoading(false);
    }
  };

  // 搜索防抖
  const searchTimeoutRef = useRef(null);
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setPage(0);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      loadUsers({ search: value, page: 1 });
    }, 400);
  };

  // 切换页码
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    loadUsers({ search: searchTerm, page: newPage + 1 });
  };

  // 切换每页数量
  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(0);
    loadUsers({ search: searchTerm, page: 1, limit: value });
  };

  useEffect(() => {
    loadUsers({ search: searchTerm, page: page + 1 });
  }, []);

  // 统计卡片数据（基于当前页用户）
  const stats = useMemo(() => {
    const totalUsers = totalCount;
    const activeUsers = users.filter((user) => user.status === 'active').length;
    const adminUsers = users.filter((user) => user.role === 'admin').length;
    const onlineUsers = users.filter((user) => Boolean(user.currentSessionId)).length;
    const totalBalance = users.reduce((sum, user) => sum + (Number(user.balance) || 0), 0);
    const totalRecharge = users.reduce((sum, user) => sum + (Number(user.totalRechargeAmount) || 0), 0);
    return { totalUsers, activeUsers, adminUsers, onlineUsers, totalBalance, totalRecharge };
  }, [users, totalCount]);

  const handleEditUser = (user) => {
    setCurrentUser({
      ...user,
      balance: user.balance ?? 0
    });
    setOpenUserDialog(true);
  };

  const handleAddUser = () => {
    setCurrentUser({
      username: '',
      phone: '',
      email: '',
      password: '',
      role: 'user',
      balance: 0,
      status: 'active'
    });
    setOpenUserDialog(true);
  };

  const handleDeleteUser = (user) => {
    setCurrentUser(user);
    setOpenDeleteDialog(true);
  };

  const handleChangePassword = (user) => {
    setCurrentUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setOpenPasswordDialog(true);
  };

  const handleForceLogout = async (user) => {
    try {
      const userId = user._id || user.id;
      updateActionLoading('logout', userId, true);
      await api.forceLogoutUser(userId);
      setUsers((prev) =>
        prev.map((item) =>
          (item._id || item.id) === (user._id || user.id)
            ? { ...item, currentSessionId: null }
            : item
        )
      );
      setSnackbar({
        open: true,
        message: `已强制用户 ${user.username} 下线`,
        severity: 'success'
      });
        } catch (error) {
      console.error('强制下线失败:', error);
            setSnackbar({
              open: true,
        message: error.userMessage || '强制下线失败，请重试',
              severity: 'error'
            });
    } finally {
      const userId = user._id || user.id;
      updateActionLoading('logout', userId, false);
    }
  };

  const handleUpdateRole = async (user, roleValue) => {
    if (!user || !roleValue) {
      return;
    }
    const userId = user._id || user.id;
    updateActionLoading('role', userId, true);
        try {
      const response = await api.updateUser(userId, { role: roleValue });
      const updated =
        response?.user || response?.data || { ...user, role: roleValue };

      setUsers((prev) =>
        prev.map((item) =>
          (item._id || item.id) === userId
            ? {
                ...item,
                ...updated,
                role: updated.role || roleValue
              }
            : item
        )
      );

      setSnackbar({
        open: true,
        message: roleValue === 'admin' ? '已设为管理员' : '角色已更新',
        severity: 'success'
      });
        } catch (error) {
      console.error('更新用户角色失败:', error);
            setSnackbar({
              open: true,
        message: error.userMessage || '更新角色失败，请稍后再试',
              severity: 'error'
            });
    } finally {
      updateActionLoading('role', userId, false);
    }
  };

  const handleResetPassword = async (user) => {
    if (!user) {
      return;
    }
    const userId = user._id || user.id;
    updateActionLoading('reset', userId, true);
    try {
      const response = await api.resetUserPassword(userId);
      const message =
        response?.message || response?.data?.message || `已重置 ${user.username} 的密码`;
      const payload = response?.data || {};

      setUsers((prev) =>
        prev.map((item) =>
          (item._id || item.id) === userId
            ? {
                ...item,
                forcePasswordReset: true,
                passwordUpdatedAt: payload.passwordUpdatedAt || new Date().toISOString()
              }
            : item
        )
      );

      setSnackbar({
        open: true,
        message: `${message}，默认密码为 123456`,
        severity: 'success'
      });
    } catch (error) {
      console.error('重置用户密码失败:', error);
      setSnackbar({
        open: true,
        message: error.userMessage || '重置密码失败，请稍后再试',
        severity: 'error'
      });
    } finally {
      updateActionLoading('reset', userId, false);
    }
  };

  const handleSaveUser = async () => {
    if (!currentUser) {
      return;
    }

    const userId = currentUser._id || currentUser.id;
    const payload = {
      username: currentUser.username?.trim(),
      email: currentUser.email?.trim() || '',
      phone: currentUser.phone?.trim() || '',
      balance: Number(currentUser.balance) || 0,
      status: currentUser.status
    };

    if (!payload.username) {
      setSnackbar({
        open: true,
        message: '用户名不能为空',
        severity: 'warning'
      });
      return;
    }

    if (!userId && !currentUser.password) {
          setSnackbar({
            open: true,
        message: '请为新用户设置密码',
        severity: 'warning'
          });
      return;
    }

    if (!userId && !payload.email && !payload.phone) {
      setSnackbar({
        open: true,
        message: '邮箱和手机号至少填写一项',
        severity: 'warning'
      });
      return;
    }

    try {
      if (userId) {
        const response = await api.updateUser(userId, payload);
        const updated =
          response?.user || response?.data || response || {
            ...currentUser,
            ...payload
          };
        const normalizedId = updated._id || updated.id || userId;

        setUsers((prev) =>
          prev.map((item) =>
            (item._id || item.id) === normalizedId
              ? {
                  ...item,
                  ...updated,
                  forcePasswordReset:
                    typeof updated.forcePasswordReset === 'boolean'
                      ? updated.forcePasswordReset
                      : item.forcePasswordReset,
                  totalRechargeAmount:
                    typeof updated.totalRechargeAmount === 'number'
                      ? updated.totalRechargeAmount
                      : item.totalRechargeAmount,
                  balance:
                    typeof updated.balance === 'number' ? updated.balance : item.balance
                }
              : item
          )
        );
        
        setSnackbar({
          open: true,
          message: '用户信息已更新',
          severity: 'success'
        });
      } else {
        const response = await api.createUser({
          ...payload,
          password: currentUser.password
        });

        const created =
          response?.user || response?.data || response || {
            ...payload,
            _id: String(Date.now())
          };

        setUsers((prev) => [
          {
            ...created,
            _id: created._id || created.id || String(Date.now()),
            createdAt: created.createdAt || new Date().toISOString(),
            totalRechargeAmount: Number(created.totalRechargeAmount) || 0,
            forcePasswordReset:
              typeof created.forcePasswordReset === 'boolean'
                ? created.forcePasswordReset
                : false
          },
          ...prev
        ]);

          setSnackbar({
            open: true,
          message: '用户创建成功',
            severity: 'success'
          });
        }
      setOpenUserDialog(false);
      setCurrentUser(null);
    } catch (error) {
      console.error('保存用户信息失败:', error);
      setSnackbar({
        open: true,
        message: error.userMessage || error.message || '保存用户信息失败',
        severity: 'error'
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!currentUser) {
      return;
    }
    const userId = currentUser._id || currentUser.id;
    try {
      await api.deleteUser(userId);
      setUsers((prev) => prev.filter((item) => (item._id || item.id) !== userId));
      setSnackbar({
        open: true,
        message: `已删除用户 ${currentUser.username}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('删除用户失败:', error);
      setSnackbar({
        open: true,
        message: error.userMessage || '删除用户失败，请重试',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setCurrentUser(null);
    }
  };

  const handleUpdatePassword = async () => {
    if (!currentUser) {
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setSnackbar({
        open: true,
        message: '新密码长度不能少于6位',
        severity: 'warning'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: '两次输入的密码不一致',
        severity: 'warning'
      });
      return;
    }

    try {
      await api.changeUserPassword(currentUser._id || currentUser.id, newPassword);
      setUsers((prev) =>
        prev.map((item) =>
          (item._id || item.id) === (currentUser._id || currentUser.id)
            ? {
                ...item,
                forcePasswordReset: false,
                passwordUpdatedAt: new Date().toISOString()
              }
            : item
        )
      );
          setSnackbar({
            open: true,
        message: `已修改用户 ${currentUser.username} 的密码`,
            severity: 'success'
          });
          setOpenPasswordDialog(false);
          setNewPassword('');
          setConfirmPassword('');
      setCurrentUser(null);
    } catch (error) {
      console.error('修改密码失败:', error);
      setSnackbar({
        open: true,
        message: error.userMessage || '修改密码失败，请稍后重试',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const renderStatCard = (title, value, helper) => (
    <SummaryCard elevation={3}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {title}
        </Typography>
        <Typography variant="h4" sx={{ color: '#00ffa3', fontWeight: 700 }}>
          {formatSummaryValue(value)}
        </Typography>
        {helper && (
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {helper}
          </Typography>
        )}
      </Stack>
    </SummaryCard>
  );

  return (
    <Box sx={{ width: '100%' }}>
      {/* 头部：标题 + 按钮 */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 1 }}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          用户管理
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => loadUsers({ search: searchTerm })}
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            刷新
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 'bold' }}
          >
            添加用户
          </Button>
        </Stack>
      </Box>

      {/* 统计卡片：移动端 2 列 */}
      <Grid container spacing={1.5} mb={2}>
        <Grid item xs={6} sm={4} md={3}>
          {renderStatCard('用户总数', stats.totalUsers, '当前筛选结果')}
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          {renderStatCard('活跃用户', stats.activeUsers, '状态正常')}
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          {renderStatCard('在线用户', stats.onlineUsers, '活跃会话')}
        </Grid>
        <Grid item xs={6} sm={4} md={3}>
          {renderStatCard('累计充值', stats.totalRecharge, '合计总额')}
        </Grid>
      </Grid>

      {/* 搜索栏 */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="搜索用户..."
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
              loadUsers({ search: e.target.value, page: 1 });
            }
          }}
          sx={{
            backgroundColor: 'rgba(0, 20, 40, 0.5)',
            borderRadius: '6px',
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
              '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
              '&.Mui-focused fieldset': { borderColor: 'primary.main' }
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* 表格 */}
      <Paper
        elevation={3}
        sx={{
          background: 'rgba(0, 30, 60, 0.7)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(0, 255, 159, 0.2)',
          borderRadius: '14px',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="300px">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: isMobile ? 900 : 'auto' }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(0, 40, 70, 0.8)' }}>
                    <StyledTableCell sx={{ width: isMobile ? 60 : 56 }}>ID</StyledTableCell>
                    <StyledTableCell>用户</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>联系</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>角</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>充额</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>余额</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>邀请</StyledTableCell>
                    <StyledTableCell>态</StyledTableCell>
                    <StyledTableCell>在线</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>通行</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>创建</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>充时</StyledTableCell>
                    <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>登录</StyledTableCell>
                    <StyledTableCell align="center">操作</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => {
                    const id = user._id || user.id;
                    const accessStatus = user.accessStatus || 'unrestricted';
                    const accessStyle = ACCESS_STATUS_STYLES[accessStatus] || {
                      bg: 'rgba(0, 188, 212, 0.15)',
                      color: '#4dd0e1',
                      border: '1px solid rgba(0, 188, 212, 0.3)'
                    };
                    const isResetting = Boolean(actionLoading.reset[id]);
                    const isForceLogout = Boolean(actionLoading.logout[id]);
                    const isRoleUpdating = Boolean(actionLoading.role?.[id]);

                    return (
                      <StyledTableRow key={id}>
                        <StyledTableCell sx={{ width: '56px' }}>
                          <Tooltip title={id} placement="top">
                            <Typography
                              sx={{
                                fontFamily: 'JetBrains Mono, Menlo, monospace',
                                fontSize: 13,
                                letterSpacing: 0.2
                              }}
                            >
                              {formatDisplayId(id)}
                            </Typography>
                          </Tooltip>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                              {user.username}
                            </Typography>
                            {user.role === 'admin' && (
                              <Typography
                                variant="caption"
                                sx={{ color: 'rgba(0, 255, 159, 0.7)', letterSpacing: 0.3 }}
                              >
                                系统管理员
                              </Typography>
                            )}
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={0.5}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-word', fontSize: isMobile ? '0.75rem' : 'inherit' }}>
                              {user.phone ? user.phone : '—'}
                            </Typography>
                            {user.email && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', wordBreak: 'break-word', display: 'block', fontSize: isMobile ? '0.65rem' : '0.75rem' }}>
                                {user.email}
                              </Typography>
                            )}
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                            <Chip
                              label={ROLE_LABELS[user.role] || '—'}
                              size="small"
                              sx={{
                                backgroundColor: user.role === 'admin' ? 'rgba(255, 0, 255, 0.2)' : 'rgba(0, 255, 159, 0.2)',
                                color: user.role === 'admin' ? '#ce93d8' : '#00ff9f',
                                fontWeight: 'bold',
                                height: isMobile ? 20 : 24,
                                fontSize: isMobile ? '0.65rem' : '0.75rem',
                                '& .MuiChip-label': { px: 0.5 }
                              }}
                            />
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ display: isMobile ? 'none' : 'table-cell', fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          {formatNumber(user.totalRechargeAmount || 0)}
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          {formatNumber(user.balance || 0)}
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: user.inviteReward > 0 ? '#00ff9f' : 'inherit', display: isMobile ? 'none' : 'table-cell', fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          {formatNumber(user.inviteReward || 0)}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={STATUS_LABELS[user.status] || '—'}
                            size="small"
                            sx={{
                              height: isMobile ? 20 : 24,
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                              backgroundColor: user.status === 'active' ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 0, 0, 0.1)',
                              color: user.status === 'active' ? '#00ff84' : '#ff4d4f',
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell sx={{ minWidth: isMobile ? 50 : 72 }}>
                          <Chip
                            label={user.currentSessionId ? '在' : '离'}
                            size="small"
                            sx={{
                              height: isMobile ? 20 : 24,
                              fontSize: isMobile ? '0.65rem' : '0.75rem',
                              backgroundColor: user.currentSessionId ? 'rgba(0, 255, 0, 0.1)' : 'rgba(128, 128, 128, 0.1)',
                              color: user.currentSessionId ? '#00ff84' : '#808080',
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        </StyledTableCell>
                        <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                            <Chip
                              label={ACCESS_STATUS_SHORT[accessStatus] || '—'}
                              size="small"
                              sx={{
                                height: isMobile ? 20 : 24,
                                fontSize: isMobile ? '0.65rem' : '0.75rem',
                                backgroundColor: accessStyle.bg,
                                color: accessStyle.color,
                                '& .MuiChip-label': { px: 0.5 }
                              }}
                            />
                            {user.forcePasswordReset && (
                              <Chip
                                icon={<SecurityIcon sx={{ fontSize: 12, color: '#ffb74d' }} />}
                                label="改"
                                size="small"
                                sx={{
                                  height: isMobile ? 20 : 24,
                                  fontSize: isMobile ? '0.6rem' : '0.7rem',
                                  backgroundColor: 'rgba(255, 183, 77, 0.15)',
                                  color: '#ffb74d',
                                  '& .MuiChip-label': { px: 0.5 }
                                }}
                              />
                            )}
                          </Stack>
                        </StyledTableCell>
                        <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell', fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                            {formatDate(user.createdAt)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell', fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                            {formatDate(user.lastRechargeAt)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell sx={{ display: isMobile ? 'none' : 'table-cell', fontSize: isMobile ? '0.7rem' : 'inherit' }}>
                          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.65rem' : 'inherit' }}>
                            {formatDate(user.lastLoginAt)}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ minWidth: isMobile ? 100 : 'auto' }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="编辑">
                              <IconButton
                                size="small"
                                onClick={() => handleEditUser(user)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': { backgroundColor: 'rgba(0, 255, 159, 0.1)' },
                                  p: isMobile ? 0.5 : 1
                                }}
                              >
                                <EditIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="改密">
                              <IconButton
                                size="small"
                                onClick={() => handleChangePassword(user)}
                                sx={{
                                  color: '#9c27b0',
                                  '&:hover': { backgroundColor: 'rgba(156, 39, 176, 0.1)' },
                                  p: isMobile ? 0.5 : 1
                                }}
                              >
                                <LockResetIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="重置">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetPassword(user)}
                                  sx={{
                                    color: '#26c6da',
                                    '&:hover': { backgroundColor: 'rgba(38, 198, 218, 0.14)' },
                                    p: isMobile ? 0.5 : 1
                                  }}
                                  disabled={isResetting}
                                >
                                  {isResetting ? (
                                    <CircularProgress size={isMobile ? 12 : 16} color="inherit" />
                                  ) : (
                                    <RestartAltIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="下线">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleForceLogout(user)}
                                  sx={{
                                    color: '#ff9800',
                                    '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.12)' },
                                    p: isMobile ? 0.5 : 1
                                  }}
                                  disabled={!user.currentSessionId || isForceLogout}
                                >
                                  {isForceLogout ? (
                                    <CircularProgress size={isMobile ? 12 : 16} color="inherit" />
                                  ) : (
                                    <LogoutIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="删除">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteUser(user)}
                                  sx={{
                                    color: '#ff4d4f',
                                    '&:hover': { backgroundColor: 'rgba(255, 77, 79, 0.12)' },
                                    p: isMobile ? 0.5 : 1
                                  }}
                                  disabled={user.role === 'admin'}
                                >
                                  <DeleteIcon sx={{ fontSize: isMobile ? '1rem' : 'small' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                  {!users.length && (
                    <StyledTableRow>
                      <StyledTableCell colSpan={isMobile ? 5 : 14} align="center">
                        暂无数据
                      </StyledTableCell>
                    </StyledTableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="每页行数:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / 共 ${count}`}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                '& .MuiTablePagination-select': {
                  color: 'white'
                },
                '& .MuiTablePagination-selectIcon': {
                  color: 'rgba(255, 255, 255, 0.5)'
                }
              }}
            />
          </>
        )}
      </Paper>

      <Dialog 
        open={openUserDialog} 
        onClose={() => setOpenUserDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 255, 159, 0.2)',
            borderRadius: '16px',
            minWidth: { xs: '95vw', sm: '520px' },
            maxWidth: { xs: '95vw', sm: '520px' },
            m: { xs: 1, sm: 'auto' }
          }
        }}
      >
        <DialogTitle
          sx={{ borderBottom: '1px solid rgba(0, 255, 159, 0.1)', color: 'primary.main' }}
        >
          {currentUser?._id || currentUser?.id ? '编辑用户' : '添加用户'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            label="用户名"
            variant="outlined"
            fullWidth
            margin="normal"
            value={currentUser?.username || ''}
            onChange={(e) => setCurrentUser((prev) => ({ ...prev, username: e.target.value }))}
            disabled={Boolean(currentUser?._id || currentUser?.id)}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
          <TextField
            label="手机号"
            variant="outlined"
            fullWidth
            margin="normal"
            placeholder="请输入11位手机号"
            value={currentUser?.phone || ''}
                onChange={(e) =>
                  setCurrentUser((prev) => ({
                    ...prev,
                    phone: e.target.value
                  }))
                }
            inputProps={{ maxLength: 11 }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                  }
            }}
          />
            </Grid>
            <Grid item xs={12} sm={6}>
          <TextField
            label="邮箱（可选）"
            variant="outlined"
            fullWidth
            margin="normal"
                type="email"
            value={currentUser?.email || ''}
                onChange={(e) =>
                  setCurrentUser((prev) => ({
                    ...prev,
                    email: e.target.value
                  }))
                }
            sx={{ 
              '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
                    '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                  }
                }}
              />
            </Grid>
          </Grid>
          {!currentUser?._id && !currentUser?.id && (
            <TextField
              label="登录密码"
              variant="outlined"
              fullWidth
              margin="normal"
              type="password"
              value={currentUser?.password || ''}
              onChange={(e) =>
                setCurrentUser((prev) => ({
                  ...prev,
                  password: e.target.value
                }))
              }
              sx={{ 
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' }
                }
              }}
            />
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="user-status-label">状态</InputLabel>
                <Select
                  labelId="user-status-label"
                  label="状态"
                  value={currentUser?.status || 'active'}
                  onChange={(e) =>
                    setCurrentUser((prev) => ({
                      ...prev,
                      status: e.target.value
                    }))
                  }
                  sx={{
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.2)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 255, 159, 0.5)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <MenuItem value="active">正常</MenuItem>
                  <MenuItem value="inactive">禁用</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <TextField
            label="余额（字数）"
            variant="outlined"
            fullWidth
            margin="normal"
            type="number"
            value={currentUser?.balance ?? 0}
            onChange={(e) =>
              setCurrentUser((prev) => ({
                ...prev,
                balance: Number(e.target.value)
              }))
            }
            sx={{ 
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(0, 255, 159, 0.2)' },
                '&:hover fieldset': { borderColor: 'rgba(0, 255, 159, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'primary.main' }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(0, 255, 159, 0.1)', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button onClick={() => setOpenUserDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            取消
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none', borderRadius: '8px' }}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 77, 79, 0.2)',
            borderRadius: '16px',
            minWidth: { xs: '95vw', sm: 400 },
            maxWidth: { xs: '95vw', sm: 400 },
            m: { xs: 1, sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ color: '#ff4d4f' }}>确认删除</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            确定要删除用户 <b>{currentUser?.username}</b> 吗？该操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(255, 77, 79, 0.1)', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button onClick={() => setOpenDeleteDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            取消
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: '#ff4d4f',
              '&:hover': { backgroundColor: '#ff7875' },
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            删除
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={openPasswordDialog} 
        onClose={() => setOpenPasswordDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 30, 60, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(156, 39, 176, 0.3)',
            borderRadius: '16px',
            minWidth: { xs: '95vw', sm: 450 },
            maxWidth: { xs: '95vw', sm: 450 },
            m: { xs: 1, sm: 'auto' }
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(156, 39, 176, 0.15)', color: '#e1bee7' }}>
          修改用户密码
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 2, color: 'rgba(255,255,255,0.7)' }}>
            正在修改用户 <b>{currentUser?.username}</b> 的密码
          </Typography>
          <TextField
            label="新密码"
            variant="outlined"
            fullWidth
            margin="normal"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(156, 39, 176, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(156, 39, 176, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#ba68c8' }
              }
            }}
          />
          <TextField
            label="确认新密码"
            variant="outlined"
            fullWidth
            margin="normal"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: 'rgba(156, 39, 176, 0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(156, 39, 176, 0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#ba68c8' }
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(156, 39, 176, 0.15)', flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button onClick={() => setOpenPasswordDialog(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            取消
          </Button>
          <Button
            onClick={handleUpdatePassword}
            variant="contained"
            sx={{
              backgroundColor: '#9c27b0',
              '&:hover': { backgroundColor: '#ab47bc' },
              textTransform: 'none',
              borderRadius: '8px'
            }}
          >
            确认修改
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement; 

